/**
 * EditStudyModal — admin-only "Edit study" dialog (punch-list item #9).
 *
 * Lets an admin rename a study and edit its identity + general info from the
 * StudyDetail header, without recreating the study:
 *   - Identity:    title (required), sponsor_name, protocol_number, therapeutic_area
 *   - General info: condition, short_description, full_description
 *
 * The backend already fully supports this — every field PATCHes
 * /api/crm/studies/:id and is validated by validateStudyFields. This is a pure
 * frontend gap; no backend/DB change.
 *
 * slug is shown read-only: a slug rename is only valid before a prescreen is
 * published and is rare, so it stays a create-time concern (avoids duplicating
 * the prescreen-lock UX here).
 *
 * Mirrors WebsiteTab's edit pattern: formFromStudy seed -> controlled inputs,
 * toPayload trims strings -> null (except title), dirty via a JSON baseline.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, X, FlaskConical } from 'lucide-react';
import api from '../../../services/api';
import { Study } from '../../types';
import { Z } from '../ui';

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400';

/** Editable identity + general-info fields (string fields default to '' for controlled inputs). */
interface EditForm {
  title: string;
  internal_title: string;
  sponsor_name: string;
  protocol_number: string;
  therapeutic_area: string;
  condition: string;
  short_description: string;
  full_description: string;
  /** CTMS (CRIO) mapping — config.crio.study_id (+ optional site_id override). */
  crio_study_id: string;
  crio_site_id: string;
}

function formFromStudy(study: Study): EditForm {
  return {
    title: study.title || '',
    internal_title: study.internal_title || '',
    sponsor_name: study.sponsor_name || '',
    protocol_number: study.protocol_number || '',
    therapeutic_area: study.therapeutic_area || '',
    condition: study.condition || '',
    short_description: study.short_description || '',
    full_description: study.full_description || '',
    crio_study_id: study.config?.crio?.study_id || '',
    crio_site_id: study.config?.crio?.site_id || '',
  };
}

/** PATCH payload: trimmed strings -> null, except title (always sent trimmed; guarded non-empty). */
function toPayload(f: EditForm) {
  const s = (v: string) => (v.trim() ? v.trim() : null);
  return {
    title: f.title.trim(),
    // Recruiter-only alias; optional on edit (empty clears it → falls back to title).
    internal_title: s(f.internal_title),
    sponsor_name: s(f.sponsor_name),
    protocol_number: s(f.protocol_number),
    therapeutic_area: s(f.therapeutic_area),
    condition: s(f.condition),
    short_description: s(f.short_description),
    full_description: s(f.full_description),
    // Empty -> null clears the CRIO mapping; backend stores under config.crio.
    crio_study_id: s(f.crio_study_id),
    crio_site_id: s(f.crio_site_id),
  };
}

export default function EditStudyModal({
  study,
  open,
  onClose,
  onSaved,
}: {
  study: Study;
  open: boolean;
  onClose: () => void;
  onSaved: (study: Study) => void;
}) {
  const [form, setForm] = useState<EditForm>(() => formFromStudy(study));
  const [baseline, setBaseline] = useState(() => JSON.stringify(toPayload(formFromStudy(study))));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Non-fatal: the study saved but a cross-tool mirror (CRIO study ID) did not.
  const [warning, setWarning] = useState<string | null>(null);

  // Re-seed from the study whenever the modal (re)opens or the study changes,
  // so a reopen never shows a stale draft from a previous edit.
  useEffect(() => {
    if (!open) return;
    const next = formFromStudy(study);
    setForm(next);
    setBaseline(JSON.stringify(toPayload(next)));
    setError(null);
    setWarning(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, study.id, study.updated_at]);

  const dirty = useMemo(() => JSON.stringify(toPayload(form)) !== baseline, [form, baseline]);
  const set = <K extends keyof EditForm>(key: K, value: EditForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const titleValid = form.title.trim().length > 0;

  const save = useCallback(async () => {
    if (!form.title.trim()) {
      setError('Study name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setWarning(null);
    try {
      const res = await api.patch(`/api/crm/studies/${study.id}`, toPayload(form));
      onSaved(res.data.study);
      // The CRIO study ID fans out to eReg + IXVerify. The study itself saved
      // either way, so this is a warning, not an error — but the modal stays
      // open so a half-applied sync is seen rather than dismissed.
      const crioWarnings: string[] | undefined = res.data.crio_sync_warnings;
      if (crioWarnings?.length) {
        setWarning(`Study saved, but the CRIO study ID did not reach every tool: ${crioWarnings.join(' ')} Saving again retries it.`);
        return;
      }
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save study');
    } finally {
      setSaving(false);
    }
  }, [study.id, form, onSaved, onClose]);

  if (!open) return null;

  return (
    <div className={`fixed inset-0 ${Z.modal} flex items-start justify-center overflow-y-auto bg-black/40 p-4`}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Edit study"
        className="my-auto w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 dark:border-gray-700 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary-50 p-2 dark:bg-primary-900/30">
              <FlaskConical className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Edit study</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Name &amp; general information</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 p-4 sm:p-6">
          <div>
            <label className={labelCls} htmlFor="edit-study-title">
              Public title <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-study-title"
              type="text"
              value={form.title}
              maxLength={300}
              className={inputCls}
              onChange={(e) => set('title', e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Patient-facing — the public website, SMS/email, and the URL slug.
            </p>
            {!titleValid && <p className="mt-1 text-xs text-red-600 dark:text-red-400">Public title is required.</p>}
          </div>

          <div>
            <label className={labelCls} htmlFor="edit-study-internal-title">Internal title</label>
            <input
              id="edit-study-internal-title"
              type="text"
              value={form.internal_title}
              maxLength={300}
              className={inputCls}
              placeholder="Optional — defaults to the public title"
              onChange={(e) => set('internal_title', e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Recruiter-only working name shown across the CRM; never shown to patients.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="edit-study-sponsor">Sponsor</label>
              <input
                id="edit-study-sponsor"
                type="text"
                placeholder="e.g. Acme Pharma"
                value={form.sponsor_name}
                maxLength={300}
                className={inputCls}
                onChange={(e) => set('sponsor_name', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="edit-study-protocol">Protocol number</label>
              <input
                id="edit-study-protocol"
                type="text"
                placeholder="e.g. ABC-123"
                value={form.protocol_number}
                maxLength={300}
                className={inputCls}
                onChange={(e) => set('protocol_number', e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="edit-study-ta">Therapeutic area</label>
              <input
                id="edit-study-ta"
                type="text"
                placeholder="e.g. Cardiology"
                value={form.therapeutic_area}
                maxLength={300}
                className={inputCls}
                onChange={(e) => set('therapeutic_area', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="edit-study-condition">Condition</label>
              <input
                id="edit-study-condition"
                type="text"
                placeholder="e.g. Type 2 Diabetes"
                value={form.condition}
                maxLength={300}
                className={inputCls}
                onChange={(e) => set('condition', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className={labelCls} htmlFor="edit-study-short">Short description</label>
            <textarea
              id="edit-study-short"
              rows={2}
              placeholder="One or two sentences summarizing the study."
              value={form.short_description}
              maxLength={300}
              className={inputCls}
              onChange={(e) => set('short_description', e.target.value)}
            />
          </div>

          <div>
            <label className={labelCls} htmlFor="edit-study-full">Full description</label>
            <textarea
              id="edit-study-full"
              rows={5}
              placeholder="The detailed write-up for this study."
              value={form.full_description}
              maxLength={10000}
              className={inputCls}
              onChange={(e) => set('full_description', e.target.value)}
            />
          </div>

          {/* CTMS (CRIO) integration mapping. Links this study to a CRIO study so
              pushed leads land in the right CRIO study. Find the ids by running
              the CRIO discovery script (or ask an admin). */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30">
            <div className="mb-2 flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">CTMS · CRIO</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="edit-study-crio">CRIO study ID</label>
                <input
                  id="edit-study-crio"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 32718"
                  value={form.crio_study_id}
                  maxLength={64}
                  className={inputCls}
                  onChange={(e) => set('crio_study_id', e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="edit-study-crio-site">CRIO site ID (optional)</label>
                <input
                  id="edit-study-crio-site"
                  type="text"
                  inputMode="numeric"
                  placeholder="defaults to CRIO_SITE_ID"
                  value={form.crio_site_id}
                  maxLength={64}
                  className={inputCls}
                  onChange={(e) => set('crio_site_id', e.target.value)}
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              Set the CRIO study ID to push this study&apos;s leads into CRIO. Leave blank to disable the push for this study.
            </p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              The study ID is shared platform-wide: saving it here also records it on the study&apos;s
              eReg record and its IXVerify study, and a change made in either of those lands back here.
              The site ID stays CRM-only.
            </p>
          </div>

          {/* slug is read-only here (rename is a create-time concern) */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30">
            <label className={labelCls}>URL slug</label>
            <p className="font-mono text-sm text-gray-700 dark:text-gray-200">{study.slug}</p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Set at creation; can&apos;t change after a prescreen is published.
            </p>
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {warning && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              {warning}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 p-4 dark:border-gray-700 sm:p-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving || !titleValid}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              'Save changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
